/**
 * Phase G+ Phase 5b (19.06.2026) — Mitarbeiter-Profil HR-Tab (REBUILD)
 * --------------------------------------------------------------------
 * Easy-to-understand vacation/absence overview with:
 *   - Live breakdown card (Vorjahr · Anspruch · Bisher · Genommen · Geplant · Verbleibend)
 *   - "Resturlaub manuell setzen" dialog for Transportlogy-migration
 *   - Three quick-add buttons (Urlaub / Krank / Frei) with mini-dialog
 *   - Pflegeurlaub / Papamonat / Sonderurlaub available via "Weitere"
 *   - Audit-tagged migration state visible at a glance
 *
 * Designed so any non-power-user can pick this up in < 5 minutes.
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Plane, HeartPulse, Clock4, AlertTriangle, Loader2, Plus, ArrowRight,
  Sparkles, Info, Calendar, Baby, BabyIcon, HelpCircle, X as XIcon, Trash2,
} from "lucide-react";

const ABSENCE_LABELS = {
  urlaub: "Urlaub",
  krankenstand: "Krankenstand",
  zeitausgleich: "Frei (Zeitausgleich)",
  sonderurlaub: "Sonderurlaub",
  unbezahlt_frei: "Unbezahlt frei",
  unentschuldigt: "Unentschuldigt",
  karenz: "Karenz",
  feiertag: "Feiertag",
  pflegeurlaub: "Pflegeurlaub",
  papamonat: "Papamonat",
  kuendigungsfrist: "Kündigungsfrist (bezahlt)",
};

const ABSENCE_COLORS = {
  urlaub: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  krankenstand: "bg-rose-500/15 text-rose-300 border-rose-500/40",
  zeitausgleich: "bg-sky-500/15 text-sky-300 border-sky-500/40",
  sonderurlaub: "bg-violet-500/15 text-violet-300 border-violet-500/40",
  pflegeurlaub: "bg-orange-500/15 text-orange-300 border-orange-500/40",
  papamonat: "bg-pink-500/15 text-pink-300 border-pink-500/40",
  unbezahlt_frei: "bg-zinc-500/15 text-zinc-300 border-zinc-500/40",
  unentschuldigt: "bg-red-700/20 text-red-300 border-red-700/40",
  karenz: "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/40",
  feiertag: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  kuendigungsfrist: "bg-yellow-500/15 text-yellow-300 border-yellow-500/40",
};

const STATUS_LABELS = {
  pending: "Ausstehend", approved: "Genehmigt",
  rejected: "Abgelehnt", cancelled: "Storniert", closed: "Abgeschlossen",
};

const QUICK_TYPES = [
  { key: "urlaub", label: "Urlaub", icon: Plane },
  { key: "krankenstand", label: "Krank", icon: HeartPulse },
  { key: "zeitausgleich", label: "Frei", icon: Clock4 },
];
const MORE_TYPES = [
  { key: "pflegeurlaub", label: "Pflegeurlaub", icon: Baby },
  { key: "papamonat", label: "Papamonat", icon: BabyIcon },
  { key: "sonderurlaub", label: "Sonderurlaub", icon: Sparkles },
  { key: "unbezahlt_frei", label: "Unbezahlt frei", icon: Calendar },
];

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("de-AT"); } catch { return iso; }
}

export default function EmployeeHrTab({ empId, emp, canEdit }) {
  const [vac, setVac] = useState(null);
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showQuickAdd, setShowQuickAdd] = useState(null); // type key
  const [showMigrate, setShowMigrate] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [v, a] = await Promise.all([
        api.get(`/hr/vacation/${empId}`),
        api.get(`/hr/absences?employee_id=${empId}&limit=200`),
      ]);
      setVac(v.data);
      setAbsences(a.data?.items || a.data || []);
    } catch (e) {
      toast.error("Konnte HR-Daten nicht laden");
    } finally {
      setLoading(false);
    }
  }, [empId]);

  useEffect(() => { reload(); }, [reload]);

  if (loading || !vac) {
    return (
      <div className="flex items-center gap-2 text-zinc-500 p-6">
        <Loader2 className="w-4 h-4 animate-spin" /> Lade Urlaubs-Daten…
      </div>
    );
  }

  const bk = vac.breakdown || {};
  const krankYtdDays = absences
    .filter(a => a.type === "krankenstand"
                 && (a.status === "approved" || a.status === "closed")
                 && (a.start_date || "") >= `${new Date().getFullYear()}-01-01`)
    .reduce((s, a) => s + (a.working_days || 0), 0);

  return (
    <div className="space-y-4" data-testid="employee-hr-tab">
      {/* ─── Migrations-Hinweis ─────────────────────────────────────── */}
      {vac.migrated_at && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-xs text-amber-200 flex items-center gap-2">
          <Info className="w-4 h-4 shrink-0" />
          <span>
            <b>Migrierter Resturlaub:</b> am {fmtDate(vac.migrated_at)} wurde
            der Stand manuell auf <b>{vac.migrated_target_remaining} Tage</b> gesetzt
            {vac.migrated_note ? <> · Notiz: <i>„{vac.migrated_note}“</i></> : null}.
            Monatlicher Aufbau ist für diesen Zyklus eingefroren.
          </span>
        </div>
      )}

      {/* ─── Balance-Karte ───────────────────────────────────────────── */}
      <div className="rounded-lg border border-zinc-800 bg-zinc-950 overflow-hidden"
           data-testid="hr-vacation-card">
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
          <div>
            <div className="font-display text-sm font-semibold text-zinc-100">Urlaubskonto</div>
            <div className="text-xs text-zinc-500">
              Zyklus {fmtDate(bk.cycle_start)} → {fmtDate(bk.cycle_end)}
              {!bk.accrual_frozen && bk.monthly_accrual_rate ? (
                <> · Aufbau {bk.monthly_accrual_rate.toLocaleString("de-AT", { maximumFractionDigits: 4 })} Tage/Monat</>
              ) : null}
              {bk.accrual_frozen ? <> · <span className="text-amber-400">Aufbau eingefroren</span></> : null}
            </div>
          </div>
          {canEdit && (
            <button
              data-testid="hr-migrate-btn"
              onClick={() => setShowMigrate(true)}
              className="inline-flex items-center gap-1.5 text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-100 px-3 py-1.5 rounded transition-colors"
              title="Für Live-Switch von Transportlogy"
            >
              <Sparkles className="w-3.5 h-3.5" /> Resturlaub setzen
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-6 gap-px bg-zinc-800">
          <BkCell label="Vorjahr-Übertrag" value={bk.vorjahr_carry_over} hint="Aus dem letzten Zyklus übertragene Resttage." />
          <BkCell label="Jahresanspruch" value={bk.entitlement_total} hint="Volles Kontingent pro Zyklus, lt. KV." />
          <BkCell label="Bisher aufgebaut" value={bk.accrued_to_date} highlight={!bk.accrual_frozen}
                  hint={bk.accrual_frozen ? "Zyklus eingefroren — gesamter Anspruch sofort verfügbar." : `Linear: ${bk.months_completed} von 12 Monaten × ${(bk.monthly_accrual_rate || 0).toFixed(4)}`} />
          <BkCell label="Genommen" value={bk.used_days} hint="Bereits konsumierte Urlaubstage (genehmigt + abgeschlossen)." />
          <BkCell label="Geplant" value={bk.planned_days} hint="Genehmigt für die Zukunft + offene Anträge." />
          <BkCell label="Verbleibend heute" value={bk.remaining_now} tone="emerald" big
                  hint="Was der Mitarbeiter HEUTE noch frei nehmen kann." />
        </div>
      </div>

      {/* ─── Krank-YTD Box ──────────────────────────────────────────── */}
      <div className="rounded-lg border border-zinc-800 bg-zinc-950 px-4 py-3 flex items-center justify-between"
           data-testid="hr-krank-box">
        <div className="flex items-center gap-2">
          <HeartPulse className="w-4 h-4 text-rose-400" />
          <div>
            <div className="text-xs text-zinc-400">Krank dieses Jahr</div>
            <div className="font-display text-xl font-bold text-zinc-100">{krankYtdDays.toFixed(1)} Tage</div>
          </div>
        </div>
        <div className="text-[11px] text-zinc-500 max-w-xs text-right">
          Stand seit {new Date().getFullYear()}-01-01. EFZG-Volle Lohnfortzahlung läuft<br/>
          aus nach ca. 6-12 Wochen je nach Dienstjahren.
        </div>
      </div>

      {/* ─── Quick-Add Buttons ───────────────────────────────────────── */}
      {canEdit && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="hr-quickadd-row">
          {QUICK_TYPES.map(t => (
            <button
              key={t.key}
              data-testid={`hr-quickadd-${t.key}`}
              onClick={() => setShowQuickAdd(t.key)}
              className="inline-flex items-center gap-2 bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-200 px-3 py-1.5 rounded text-sm border border-emerald-600/40"
            >
              <t.icon className="w-3.5 h-3.5" /> + {t.label}
            </button>
          ))}
          <details className="relative inline-block">
            <summary className="list-none cursor-pointer inline-flex items-center gap-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 px-3 py-1.5 rounded text-sm">
              <Plus className="w-3.5 h-3.5" /> Weitere
            </summary>
            <div className="absolute mt-1 left-0 z-10 bg-zinc-950 border border-zinc-800 rounded-md shadow-lg min-w-[200px]">
              {MORE_TYPES.map(t => (
                <button key={t.key}
                        data-testid={`hr-quickadd-${t.key}`}
                        onClick={() => setShowQuickAdd(t.key)}
                        className="w-full text-left px-3 py-2 text-xs hover:bg-zinc-900 flex items-center gap-2 text-zinc-200">
                  <t.icon className="w-3.5 h-3.5 text-zinc-400" /> {t.label}
                </button>
              ))}
            </div>
          </details>
        </div>
      )}

      {/* ─── Absences List ──────────────────────────────────────────── */}
      <div className="rounded-lg border border-zinc-800 bg-zinc-950 overflow-hidden">
        <div className="px-4 py-2 border-b border-zinc-800 text-xs text-zinc-400 font-semibold">
          Alle Abwesenheiten ({absences.length})
        </div>
        {absences.length === 0 ? (
          <div className="p-4 text-xs text-zinc-500 text-center">
            Noch keine Abwesenheiten erfasst.
          </div>
        ) : (
          <table className="w-full text-xs" data-testid="hr-absences-table">
            <thead>
              <tr className="bg-zinc-900/50 text-zinc-400">
                <th className="text-left px-3 py-2">Typ</th>
                <th className="text-left px-3 py-2">Von</th>
                <th className="text-left px-3 py-2">Bis</th>
                <th className="text-right px-3 py-2">Tage</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Notiz</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {absences.slice(0, 50).map(a => (
                <tr key={a.id} className="border-t border-zinc-800 hover:bg-zinc-900/40"
                    data-testid={`hr-absence-${a.id}`}>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border ${ABSENCE_COLORS[a.type] || "border-zinc-700 text-zinc-300"}`}>
                      {ABSENCE_LABELS[a.type] || a.type}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-zinc-300">{fmtDate(a.start_date)}</td>
                  <td className="px-3 py-2 text-zinc-300">{fmtDate(a.end_date)}</td>
                  <td className="px-3 py-2 text-right text-zinc-300">{(a.working_days || 0).toFixed(1)}</td>
                  <td className="px-3 py-2 text-zinc-300">{STATUS_LABELS[a.status] || a.status}</td>
                  <td className="px-3 py-2 text-zinc-400 max-w-xs truncate" title={a.note || ""}>{a.note || "—"}</td>
                  <td className="px-3 py-2 text-right">
                    {canEdit && (a.status === "pending" || a.status === "approved") && (
                      <button
                        onClick={async () => {
                          if (!confirm(`Diese ${ABSENCE_LABELS[a.type] || a.type}-Abwesenheit wirklich löschen?`)) return;
                          try {
                            await api.delete(`/hr/absences/${a.id}`);
                            toast.success("Eintrag gelöscht");
                            reload();
                          } catch (e) {
                            toast.error(e?.response?.data?.detail || "Fehler beim Löschen");
                          }
                        }}
                        className="text-zinc-500 hover:text-rose-400"
                        title="Löschen"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ─── Migrate Dialog ─────────────────────────────────────────── */}
      {showMigrate && (
        <MigrateDialog
          empId={empId}
          current={bk.remaining_now || 0}
          onClose={() => setShowMigrate(false)}
          onDone={() => { setShowMigrate(false); reload(); }}
        />
      )}

      {/* ─── Quick-Add Dialog ───────────────────────────────────────── */}
      {showQuickAdd && (
        <QuickAddDialog
          empId={empId}
          type={showQuickAdd}
          label={ABSENCE_LABELS[showQuickAdd] || showQuickAdd}
          onClose={() => setShowQuickAdd(null)}
          onDone={() => { setShowQuickAdd(null); reload(); }}
        />
      )}
    </div>
  );
}

function BkCell({ label, value, hint, tone, highlight, big }) {
  const toneCls = {
    emerald: "text-emerald-300",
    amber: "text-amber-300",
  }[tone] || "text-zinc-100";
  return (
    <div className="bg-zinc-950 px-3 py-2 group" title={hint || ""}>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-zinc-500">
        {label}
        {hint && <HelpCircle className="w-3 h-3 opacity-50 group-hover:opacity-100" />}
      </div>
      <div className={`mt-0.5 font-display font-bold ${big ? "text-2xl" : "text-base"} ${toneCls} ${highlight ? "underline decoration-dotted underline-offset-2 decoration-zinc-700" : ""}`}>
        {typeof value === "number" ? value.toLocaleString("de-AT", { minimumFractionDigits: value % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 }) : "—"}
      </div>
    </div>
  );
}

function MigrateDialog({ empId, current, onClose, onDone }) {
  const [val, setVal] = useState(current?.toString() || "");
  const [note, setNote] = useState("Migration aus Transportlogy");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const n = parseFloat(val.replace(",", "."));
    if (isNaN(n) || n < 0) {
      toast.error("Bitte eine Zahl ≥ 0 eingeben");
      return;
    }
    setSaving(true);
    try {
      const res = await api.patch(`/hr/vacation/${empId}/migrate-balance`, {
        remaining_days: n,
        note: note.trim() || null,
      });
      toast.success(`Resturlaub gesetzt: ${res.data.new_remaining} Tage`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Setzen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal onClose={onClose} title="Resturlaub manuell setzen" testid="hr-migrate-dialog">
      <p className="text-xs text-zinc-400 leading-relaxed">
        Beim <b>Wechsel von Transportlogy</b> trägst du hier ein, wie viele Urlaubstage
        der Mitarbeiter HEUTE noch übrig hat. Die Platform übernimmt diesen Wert
        1:1 als Stand für den aktuellen Zyklus — der monatliche Aufbau wird
        eingefroren, damit der Wert stabil bleibt.
      </p>
      <label className="block text-xs text-zinc-400 mt-3">Resturlaub-Tage (heute)</label>
      <input
        data-testid="hr-migrate-input"
        type="number" step="0.5" min="0"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        className="w-full mt-1 px-3 py-2 bg-zinc-900 border border-zinc-700 rounded text-zinc-100"
      />
      <label className="block text-xs text-zinc-400 mt-3">Notiz (optional)</label>
      <input
        value={note}
        onChange={(e) => setNote(e.target.value)}
        className="w-full mt-1 px-3 py-2 bg-zinc-900 border border-zinc-700 rounded text-zinc-100 text-sm"
      />
      <div className="mt-4 flex items-center justify-end gap-2">
        <button onClick={onClose} className="text-xs text-zinc-400 hover:text-zinc-200 px-3 py-1.5">
          Abbrechen
        </button>
        <button
          data-testid="hr-migrate-save"
          onClick={submit}
          disabled={saving}
          className="inline-flex items-center gap-1 text-xs bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-1.5 rounded"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
          Speichern
        </button>
      </div>
    </Modal>
  );
}

function QuickAddDialog({ empId, type, label, onClose, onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (from > to) {
      toast.error("Start muss vor Ende liegen");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/hr/absences`, {
        employee_id: empId,
        type,
        start_date: from,
        end_date: to,
        note: note.trim() || null,
        status: "approved",   // Quick-Add = direkte Genehmigung durch Admin
      });
      toast.success(`${label} eingetragen`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal onClose={onClose} title={`+ ${label}`} testid={`hr-quickadd-${type}-dialog`}>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-zinc-400">Von</label>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
                 data-testid="hr-quickadd-from"
                 className="w-full mt-1 px-3 py-2 bg-zinc-900 border border-zinc-700 rounded text-zinc-100 text-sm"/>
        </div>
        <div>
          <label className="block text-xs text-zinc-400">Bis</label>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
                 data-testid="hr-quickadd-to"
                 className="w-full mt-1 px-3 py-2 bg-zinc-900 border border-zinc-700 rounded text-zinc-100 text-sm"/>
        </div>
      </div>
      <label className="block text-xs text-zinc-400 mt-3">Notiz (optional)</label>
      <input value={note} onChange={(e) => setNote(e.target.value)}
             data-testid="hr-quickadd-note"
             placeholder="z.B. Familienurlaub"
             className="w-full mt-1 px-3 py-2 bg-zinc-900 border border-zinc-700 rounded text-zinc-100 text-sm"/>
      <div className="mt-4 flex items-center justify-end gap-2">
        <button onClick={onClose} className="text-xs text-zinc-400 hover:text-zinc-200 px-3 py-1.5">
          Abbrechen
        </button>
        <button data-testid="hr-quickadd-save"
                onClick={submit} disabled={saving}
                className="inline-flex items-center gap-1 text-xs bg-emerald-600 hover:bg-emerald-500 text-white px-3 py-1.5 rounded">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
          Speichern
        </button>
      </div>
    </Modal>
  );
}

function Modal({ children, title, onClose, testid }) {
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
         onClick={onClose} data-testid={testid}>
      <div className="bg-zinc-950 border border-zinc-800 rounded-lg max-w-md w-full p-5"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-base font-semibold text-zinc-100">{title}</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200">
            <XIcon className="w-4 h-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
